<nav class="navbar navbar-dark d-flex d-lg-none justify-content-between align-items-center w-100 px-2">
  <!-- Menu toggle button on the left -->
  <button (click)="openMainMenu()" class="navbar-toggler border-0" type="button">
    <fa-icon icon="bars"></fa-icon>
  </button>

  <!-- Logo right after the hamburger menu with 50% opacity -->
  <a (click)="onLogoClick($event)" class="navbar-brand position-relative">
    <img alt="AstroBin" src="assets/images/nav-logo.png?v=3" />
    <span *ngIf="versionCheckService.updateAvailable$ | async" class="version-update-badge">
      <fa-icon icon="circle-exclamation"></fa-icon>
    </span>
  </a>

  <!-- Spacer div to push the right controls to the edge -->
  <div class="flex-grow-1"></div>

  <!-- Right side controls -->
  <div class="right-controls d-flex align-items-center">
    <!-- For non-PWA mode, add search icon -->
    <ng-container *ngIf="(isPwaMode$ | async) === false">
      <!-- Search link -->
      <a class="nav-link" routerLink="/search" routerLinkActive="active">
        <fa-icon icon="search"></fa-icon>
      </a>
    </ng-container>

    <!-- Messages button - only visible when logged in -->
    <ng-container *ngIf="user">
      <a class="nav-link position-relative" routerLink="/messages/inbox">
        <fa-icon icon="message"></fa-icon>
        <ng-container *ngIf="unreadMessagesCount$ | async as unreadCount">
          <span *ngIf="!!unreadCount" class="badge bg-danger rounded-pill">
            {{ unreadCount }}
          </span>
        </ng-container>
      </a>
    </ng-container>

    <!-- Notifications button - only visible when logged in (for both PWA and non-PWA) -->
    <ng-container *ngIf="user">
      <a (click)="openNotificationsOffcanvas()" class="nav-link position-relative">
        <fa-icon icon="bell"></fa-icon>
        <ng-container *ngIf="unreadNotificationsCount$ | async as unreadCount">
          <span *ngIf="!!unreadCount" class="badge bg-danger rounded-pill">
            {{ unreadCount }}
          </span>
        </ng-container>
      </a>
    </ng-container>

    <!-- Upload button - only visible when logged in and in PWA mode -->
    <ng-container *ngIf="user && (isPwaMode$ | async)">
      <a class="nav-link" routerLink="/uploader" routerLinkActive="active">
        <fa-icon icon="upload"></fa-icon>
      </a>
    </ng-container>

    <!-- Animated container for avatar (always present when logged in and not PWA) -->
    <div *ngIf="user && (isPwaMode$ | async) === false" class="avatar-animated-menu-container">
      <!-- User avatar - always rendered, animation controls visibility -->
      <div class="animated-item" [@slideInOut]="!isOnCurrentUserGalleryPage ? 'in' : 'out'">
        <a (click)="openUserMenu()" class="nav-link user-avatar-link">
          <div class="avatar-container">
            <astrobin-avatar [link]="false" [user]="user"></astrobin-avatar>
          </div>
        </a>
      </div>

      <!-- Kebab menu - only renders on user's gallery with a page menu -->
      <div
        *ngIf="isOnCurrentUserGalleryPage && (hasPageMenu$ | async)"
        @fadeInOut
        [attr.data-menu-id]="getCurrentMenuId()"
        class="animated-item kebab-menu-container position-relative"
      >
        <a (click)="openPageMenu()" class="nav-link kebab-menu ms-2">
          <fa-icon icon="ellipsis-v"></fa-icon>
        </a>
      </div>
    </div>

    <!-- Login button for non-logged in users (in both PWA and non-PWA modes) -->
    <ng-container *ngIf="!user">
      <a (click)="openUserMenu()" class="nav-link">
        <fa-icon icon="sign-in-alt"></fa-icon>
      </a>
    </ng-container>

    <!-- Show regular kebab (no animation) on pages with menu but not user's gallery and not PWA -->
    <div
      *ngIf="(hasPageMenu$ | async) && !isOnCurrentUserGalleryPage && (isPwaMode$ | async) === false"
      [attr.data-menu-id]="getCurrentMenuId()"
      class="kebab-menu-container position-relative"
    >
      <a (click)="openPageMenu()" class="nav-link kebab-menu ms-2">
        <fa-icon icon="ellipsis-v"></fa-icon>
      </a>
    </div>

    <!-- PWA kebab menu (always visible when there's a page menu in PWA mode) -->
    <div
      *ngIf="(hasPageMenu$ | async) && (isPwaMode$ | async)"
      [attr.data-menu-id]="getCurrentMenuId()"
      class="kebab-menu-container position-relative"
    >
      <a (click)="openPageMenu()" class="nav-link kebab-menu ms-2">
        <fa-icon icon="ellipsis-v"></fa-icon>
      </a>
    </div>
  </div>
</nav>

<!-- Menu Offcanvas -->
<ng-template #menuOffcanvas let-offcanvas>
  <!-- Offcanvas Header -->
  <div class="offcanvas-header">
    <!-- Back button (only shown in submenus) -->
    <button
      *ngIf="currentMenuView !== 'main'"
      (click)="showMainMenu()"
      class="btn btn-link btn-no-block text-white p-0 me-2"
    >
      <fa-icon icon="chevron-left"></fa-icon>
    </button>

    <!-- Title changes based on current view -->
    <h5 class="offcanvas-title">
      <ng-container [ngSwitch]="currentMenuView">
        <ng-container *ngSwitchCase="'main'">
          <a (click)="offcanvas.dismiss()" class="title-home-link" routerLink="/">AstroBin</a>
        </ng-container>
        <ng-container *ngSwitchCase="'language'">{{ "Language" | translate }}</ng-container>
        <ng-container *ngSwitchCase="'help'">{{ "Help" | translate }}</ng-container>
        <ng-container *ngSwitchCase="'moderate'">{{ "Moderate" | translate }}</ng-container>
      </ng-container>
    </h5>

    <!-- Close button -->
    <button (click)="offcanvas.dismiss()" class="btn-close text-reset" type="button"></button>
  </div>

  <!-- Offcanvas Body with Dynamic Content -->
  <div class="offcanvas-body d-flex flex-column">
    <!-- Main Menu View -->
    <ng-container *ngIf="currentMenuView === 'main'">
      <!-- Main navigation options -->
      <div [class.view-transition]="isInViewTransition()" class="main-nav offcanvas-menu">
        <ul class="nav flex-column">
          <!-- Home link -->
          <li class="nav-item">
            <a (click)="offcanvas.dismiss()" class="nav-link" routerLink="/">
              <fa-icon icon="home"></fa-icon>
              {{ "Home" | translate }}
            </a>
          </li>

          <!-- Search link -->
          <li class="nav-item">
            <a (click)="offcanvas.dismiss()" class="nav-link" routerLink="/search">
              <fa-icon icon="search"></fa-icon>
              {{ "Search" | translate }}
            </a>
          </li>

          <!-- Forums link -->
          <li class="nav-item">
            <a (click)="offcanvas.dismiss()" class="nav-link" routerLink="/forum">
              <fa-icon icon="comments"></fa-icon>
              {{ "Forums" | translate }}
            </a>
          </li>

          <!-- Marketplace link -->
          <li class="nav-item">
            <a [routerLink]="['/equipment/marketplace']" (click)="offcanvas.dismiss()" class="nav-link">
              <fa-icon icon="shopping-cart"></fa-icon>
              {{ "Marketplace" | translate }}
            </a>
          </li>

          <li class="nav-item">
            <a [routerLink]="['equipment', 'explorer']" (click)="offcanvas.dismiss()" class="nav-link">
              <fa-icon icon="camera-retro"></fa-icon>
              {{ "Equipment database" | translate }}
            </a>
          </li>

          <li class="nav-item">
            <a [routerLink]="['explore', 'constellations']" (click)="offcanvas.dismiss()" class="nav-link">
              <fa-icon icon="star"></fa-icon>
              {{ "Constellations" | translate }}
            </a>
          </li>

          <li class="nav-item">
            <a [routerLink]="['explore', 'iotd-tp-archive']" (click)="offcanvas.dismiss()" class="nav-link">
              <fa-icon icon="arrow-up"></fa-icon>
              {{ "IOTD/TP Archive" | translate }}
            </a>
          </li>

          <li class="nav-item">
            <a [routerLink]="['explore', 'astrophotographers-list']" (click)="offcanvas.dismiss()" class="nav-link">
              <fa-icon icon="chart-bar"></fa-icon>
              {{ "Astrophotographers list" | translate }}
            </a>
          </li>

          <!-- Upload link -->
          <li class="nav-item">
            <a (click)="offcanvas.dismiss()" class="nav-link" routerLink="/uploader">
              <fa-icon icon="upload"></fa-icon>
              {{ "Upload image or video" | translate }}
            </a>
          </li>
        </ul>
      </div>

      <!-- Bottom section with language selector and help -->
      <div [class.view-transition]="isInViewTransition()" class="bottom-nav offcanvas-menu mt-auto">
        <ul class="nav flex-column">
          <!-- Moderate menu item for authorized users with separator -->
          <ng-container *ngIf="user | isContentModerator">
            <hr class="my-2" />
            <li class="nav-item">
              <a (click)="showModerateMenu()" class="nav-link">
                <fa-icon icon="gavel"></fa-icon>
                {{ "Moderate" | translate }}
                <fa-icon class="flex-grow-1 text-end me-0" icon="chevron-right"></fa-icon>
              </a>
            </li>
          </ng-container>

          <li class="nav-item">
            <a (click)="showLanguageMenu()" class="nav-link">
              <fa-icon icon="globe"></fa-icon>
              {{ currentLanguageCodeDisplay }}
              <fa-icon class="flex-grow-1 text-end me-0" icon="chevron-right"></fa-icon>
            </a>
          </li>

          <li class="nav-item">
            <a (click)="showHelpMenu()" class="nav-link">
              <fa-icon icon="question-circle"></fa-icon>
              {{ "Help" | translate }}
              <fa-icon class="flex-grow-1 text-end me-0" icon="chevron-right"></fa-icon>
            </a>
          </li>
        </ul>

        <!-- Copyright and build information -->
        <div class="copyright-info text-center mt-4 pt-3 text-muted small">
          <div>AstroBin, &copy; 2010 - {{ currentYear }} &middot; Build: {{ buildVersion | slice: 0 : 8 }}</div>
        </div>
      </div>
    </ng-container>

    <!-- Language Menu View -->
    <ng-container *ngIf="currentMenuView === 'language'">
      <ul [class.view-transition]="isInViewTransition()" class="nav flex-column language-menu offcanvas-menu">
        <ng-container *ngFor="let language of languages">
          <li *ngIf="language.code !== '-'" class="nav-item">
            <a [href]="getSetLanguageUrl(language.code)" class="nav-link">
              {{ language.label }}
              <fa-icon
                *ngIf="language.code === translateService.currentLang"
                class="flex-grow-1 text-end text-white me-0"
                icon="check"
              ></fa-icon>
            </a>
          </li>
        </ng-container>

        <li class="nav-item mt-3">
          <a [attr.href]="helpWithTranslationsUrl" class="nav-link text-primary" target="_blank">
            <fa-icon icon="tasks"></fa-icon>
            {{ "Help with translations!" | translate }}
          </a>
        </li>
      </ul>
    </ng-container>

    <!-- Help Menu View -->
    <ng-container *ngIf="currentMenuView === 'help'">
      <ul [class.view-transition]="isInViewTransition()" class="nav flex-column help-menu offcanvas-menu">
        <li class="nav-item">
          <a class="nav-link" routerLink="/about/features">
            <fa-icon icon="info"></fa-icon>
            {{ "What is this site?" | translate }}
          </a>
        </li>
        <li class="nav-item">
          <a [href]="classicRoutesService.FAQ" class="nav-link">
            <fa-icon icon="question"></fa-icon>
            {{ "FAQ" | translate }}
          </a>
        </li>
        <li class="nav-item">
          <a [href]="classicRoutesService.SPONSORS" class="nav-link">
            <fa-icon icon="users"></fa-icon>
            {{ "Sponsors & Partners" | translate }}
          </a>
        </li>
        <li class="nav-item">
          <a [href]="classicRoutesService.CONTACT" class="nav-link">
            <fa-icon icon="envelope"></fa-icon>
            {{ "Support" | translate }}
          </a>
        </li>
      </ul>
    </ng-container>

    <!-- Moderate Menu View -->
    <ng-container *ngIf="currentMenuView === 'moderate'">
      <ul [class.view-transition]="isInViewTransition()" class="nav offcanvas-menu flex-column moderate-menu">
        <!-- Images section -->
        <li class="section-header">
          <h6>{{ "Images" | translate }}</h6>
        </li>

        <li *ngIf="user | isImageModerator" class="nav-item">
          <a [href]="classicRoutesService.MODERATE_IMAGE_QUEUE" class="nav-link">
            <fa-icon icon="images"></fa-icon>
            {{ "Image queue" | translate }}
          </a>
        </li>

        <li *ngIf="(user | isImageModerator) && (user | isSuperUser)" class="nav-item">
          <a [href]="classicRoutesService.MODERATE_SPAM_QUEUE" class="nav-link">
            <fa-icon icon="flag"></fa-icon>
            {{ "Spam list" | translate }}
          </a>
        </li>

        <!-- IOTD section -->
        <li *ngIf="user | isIotdStaff" class="section-header">
          <h6>{{ "Image of the day" | translate }}</h6>
        </li>

        <li *ngIf="user | isIotdSubmitter" class="nav-item">
          <a (click)="offcanvas.dismiss()" class="nav-link" routerLink="/iotd/submission-queue">
            <fa-icon icon="arrow-up"></fa-icon>
            {{ "Submission queue" | translate }}
          </a>
        </li>

        <li *ngIf="user | isIotdReviewer" class="nav-item">
          <a (click)="offcanvas.dismiss()" class="nav-link" routerLink="/iotd/review-queue">
            <fa-icon icon="star"></fa-icon>
            {{ "Review queue" | translate }}
          </a>
        </li>

        <li *ngIf="user | isIotdJudge" class="nav-item">
          <a (click)="offcanvas.dismiss()" class="nav-link" routerLink="/iotd/judgement-queue">
            <fa-icon icon="hammer"></fa-icon>
            {{ "Judgement queue" | translate }}
          </a>
        </li>

        <!-- Equipment section -->
        <li *ngIf="user | isEquipmentModerator" class="section-header">
          <h6>{{ "Equipment" | translate }}</h6>
        </li>

        <li *ngIf="user | isEquipmentModerator" class="nav-item">
          <a (click)="offcanvas.dismiss()" class="nav-link" routerLink="/equipment/migration-tool">
            <fa-icon icon="arrow-right"></fa-icon>
            {{ "Migration tool" | translate }}
          </a>
        </li>
      </ul>
    </ng-container>
  </div>
</ng-template>

<!-- Login/Register offcanvas template -->
<ng-template #loginRegisterMenu let-offcanvas>
  <div class="offcanvas-header">
    <h5 class="offcanvas-title">{{ "Account" | translate }}</h5>
    <button (click)="offcanvas.dismiss()" class="btn-close text-reset" type="button"></button>
  </div>
  <div class="offcanvas-body">
    <div class="d-grid gap-3">
      <a (click)="offcanvas.dismiss()" class="btn btn-primary" routerLink="/account/login">
        <fa-icon class="me-2" icon="sign-in-alt"></fa-icon>
        {{ "Log in" | translate }}
      </a>
      <a (click)="offcanvas.dismiss()" class="btn btn-outline-primary" routerLink="/account/register">
        <fa-icon class="me-2" icon="user-plus"></fa-icon>
        {{ "Register" | translate }}
      </a>
    </div>
  </div>
</ng-template>

<!-- Notifications offcanvas template -->
<ng-template #notificationsOffcanvas let-offcanvas>
  <div class="offcanvas-header justify-content-start">
    <button (click)="offcanvas.close()" class="btn btn-link btn-no-block text-white p-0 me-3">
      <fa-icon icon="chevron-left"></fa-icon>
    </button>
    <h5 class="offcanvas-title">{{ "Notifications" | translate }}</h5>
  </div>
  <div class="offcanvas-body">
    <astrobin-notifications-list [standalone]="true" (allNotificationsMarkedAsRead)="offcanvas.close()">
    </astrobin-notifications-list>
  </div>
</ng-template>

<!-- Kebab menu tooltip - positioned outside nav to avoid overflow clipping -->
<div *ngIf="showKebabMenuTooltip" class="kebab-menu-tooltip-overlay">
  <div class="kebab-menu-tooltip">
    <div class="tooltip-content">
      <div class="d-flex align-items-center mb-2">
        <fa-icon class="me-2 text-info" icon="info-circle"></fa-icon>
        <p class="mb-0">
          {{ "Tap here for page-specific options and actions" | translate }}
        </p>
      </div>
      <div class="d-flex justify-content-center mt-2">
        <button (click)="dismissKebabMenuTooltip()" class="btn btn-sm btn-primary" type="button">
          {{ "Got it" | translate }}
        </button>
      </div>
    </div>
  </div>
</div>

<!-- Version update modal -->
<ng-template #versionUpdateModal let-modal>
  <div class="modal-header">
    <h5 class="modal-title">{{ "Update available" | translate }}</h5>
    <button (click)="modal.dismiss()" class="btn-close" type="button"></button>
  </div>
  <div class="modal-body">
    <p class="mb-0">
      {{
        "A new version of AstroBin is available. Please refresh the page to get the latest features and improvements."
          | translate
      }}
    </p>
  </div>
  <div class="modal-footer">
    <button (click)="onIgnoreUpdateClick()" class="btn btn-outline-secondary" type="button">
      {{ "Ignore" | translate }}
    </button>
    <button (click)="onRefreshClick()" class="btn btn-primary" type="button">
      {{ "Refresh" | translate }}
    </button>
  </div>
</ng-template>

<!-- Version update offcanvas -->
<ng-template #versionUpdateOffcanvas let-offcanvas>
  <div class="offcanvas-header">
    <h5 class="offcanvas-title">{{ "Update available" | translate }}</h5>
    <button (click)="offcanvas.dismiss()" class="btn-close text-reset" type="button"></button>
  </div>
  <div class="offcanvas-body">
    <p class="mb-4">
      {{
        "A new version of AstroBin is available. Please refresh the page to get the latest features and improvements."
          | translate
      }}
    </p>
    <div class="d-grid gap-2">
      <button (click)="onRefreshClick()" class="btn btn-primary" type="button">
        {{ "Refresh" | translate }}
      </button>
      <button (click)="onIgnoreUpdateClick()" class="btn btn-outline-secondary" type="button">
        {{ "Ignore" | translate }}
      </button>
    </div>
  </div>
</ng-template>
